import { enrichAIFlowWithImpactScore } from '@src/aiFlow/impactScore';
import { reelPlaceholderTitle } from '@src/services/reel/reelPlaceholderTitle';
import {
  averageImpactScores,
  destructureImpactScore,
} from '@src/utility/impactScore';
import { createSpeakerFilteredTranscript } from '@src/utility/speakerUtils';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { toJS } from 'mobx';
import { z } from 'zod';

import { extractErrorInfo } from '../utility/error';
import { LogManager } from '../utility/Logger';
import {
  equalsIgnoringCase,
  removePunctuation,
  wordOverlapSimilarity,
} from '../utility/strings';
import { extractTranscriptionText } from '../utility/transcriptFormatUtils';
import { calculateSpeechPace } from '../utility/transcriptGrouping';
import { AIClipProcessor } from './AIClipProcessor';
import { backgroundVideoSaveService } from './BackgroundVideoSaveService';
import { clipProcessorDepsFromStore } from './clipProcessorDepsFromStore';
import { generateTempClipId } from './WriteQueueService';

import type { AIFlowStep } from '../aiFlow/AIFlowStep';
import type {
  ClipMetadata,
  OtherVideo,
  ReelSectionOrigin,
  SourcePlatform,
  StoryVideo,
  Video,
  VideoAction,
} from '../types.ts/story';
import type { TranscriptElement } from '../types.ts/transcript';
import type { LightweightVideoDTO } from './WriteQueueService';
import type { AudienceOption } from '@src/components/common/AudienceSelector';
import type { GoalOption } from '@src/components/common/GoalSelector';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { SceneDetection } from '@src/editor/types/sceneDetection';
import type { IClipProcessorDeps } from '@src/services/IClipProcessorDeps';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { ClipProducerPreset } from '@src/types.ts/ai_prompts';

export type ClipFragment = {
  title: string;
  text: string;
  duration: number;
  startTime: number;
  src: string;
  muxPlaybackId?: string;
  platform?: string;
  theme: string;
  themeDisplayName?: string;
  transcriptPosition: {
    startIndex: number;
    endIndex: number;
    // in case startIndex/endIndex is derived from alternative transcript
    // source (e.g. save as clip might use karaoke lines)
    relevantElements?: TranscriptElement[];
  };
  impactScore?: Record<string, number>; // Optional, if related prompt is used
  speakerName?: string; // Optional speaker name for filtered clips
  speakerLabel?: string; // Optional speaker label for filtered clips
  isReel?: boolean; // Optional, if this clip is a reel: compilation of multiple clips
  videoTemplateId?: string; // Optional video template ID for branding
  customPrompt?: string; // Optional custom prompt used to generate this clip
  suggestedTheme?: string; // Optional suggested topic used to generate this clip
  durationRelaxed?: boolean; // True if this clip was outside the requested duration range
  /** Where this piece came from, when it was contributed by an existing clip
   *  or reel rather than cut fresh from the transcript (PLAT-11067). */
  origin?: ReelSectionOrigin[];
};

export type TranscriptClipFragment = Pick<ClipFragment, 'transcriptPosition'> &
  Partial<ClipFragment>;

export type GenerationContext = {
  themes?: string[];
  customPrompt?: string;
  suggestedTheme?: string | null;
  duration?: number | null;
  speakers?: string[];
  goal?: string | null;
  audiences?: string[];
};

// Result type for saveProducedClips when using background queue
export type SaveProducedClipsResult =
  | { mode: 'sync'; savedCount: number; savedIds: string[] }
  | { mode: 'queued'; queuedCount: number; tempIds: string[] };

// Configuration for clip production — assembled from feature flags and user settings
export interface ClipProducerConfig {
  /** Verify AI-returned text matches transcript at resolved indices */
  enableTextVerification: boolean;
  enableSpeakerFiltering: boolean;
  /** Max words per transcript chunk submitted to the AI */
  chunkSize: number;
}

const defaultClipProducerConfig: ClipProducerConfig = {
  enableTextVerification: false,
  enableSpeakerFiltering: true,
  chunkSize: 2000,
};

// Options for saveProducedClips
export interface SaveProducedClipsOptions {
  enableLightweightClipSave?: boolean;
  useBackgroundQueue?: boolean;
  reelTitle?: string;
}

const MAX_WORDS_OVERLAP = 300;

export const logger = LogManager.getLogger('AIClipProducer');

const getFlowTitleAndCategoryByTheme = (
  clipProducerPresets: ClipProducerPreset[] | null,
  theme: string,
) => {
  return clipProducerPresets?.find(preset => preset.title === theme)!;
};

const getRangeForDuration = (duration: number) =>
  ({
    7: [5, 12],
    15: [7, 28],
    30: [16, 49],
    60: [34, 80],
    90: [65, 120],
    120: [75, 150],
  })[duration] || [0.7 * duration, 1.3 * duration];

function chunkTranscription(
  words: string[],
  chunkSize: number,
  overlap: number,
): { text: string; index: number }[] {
  const chunks: { text: string; index: number }[] = [];

  for (let i = 0; i < words.length; i += chunkSize - overlap) {
    const chunkWords = words.slice(i, i + chunkSize);
    chunks.push({
      text: chunkWords.join(' '),
      index: i, // word index — useful for mapping later
    });
    if (i + chunkSize >= words.length) break;
  }

  return chunks;
}

export function mapWordsToElements(
  words: string[],
  elements: TranscriptElement[],
  speakerLabels: string[] | null,
): number[] {
  const mapping: number[] = [];
  let wordIdx = 0;
  for (let i = 0; i < elements.length && wordIdx < words.length; i++) {
    const el = elements[i];
    if (el.state === 'removed' || el.type !== 'text') continue;
    if (speakerLabels && !speakerLabels.includes(el.speaker_label ?? ''))
      continue;
    if (
      el.value &&
      equalsIgnoringCase(
        removePunctuation(el.value),
        removePunctuation(words[wordIdx]),
      )
    ) {
      mapping.push(i);
      wordIdx++;
    }
  }
  return mapping;
}

// Two clips are treated as the same segment when their transcript ranges
// overlap by at least this fraction of the shorter clip. Catches exact dupes
// (ratio 1.0), full containment, and heavy partial overlap from the ~300-word
// chunk overlap window — while leaving genuinely distinct adjacent segments
// that only share their opening words.
const OVERLAP_DEDUP_THRESHOLD = 0.7;

type TranscriptRange = ClipFragment['transcriptPosition'];

function rangeLength(range: TranscriptRange): number {
  return range.endIndex - range.startIndex + 1;
}

// Fraction of the shorter range that overlaps the other (0 = disjoint, 1 =
// shorter range fully contained in the longer).
function overlapRatio(a: TranscriptRange, b: TranscriptRange): number {
  const overlap =
    Math.min(a.endIndex, b.endIndex) - Math.max(a.startIndex, b.startIndex) + 1;
  if (overlap <= 0) return 0;
  const shorter = Math.min(rangeLength(a), rangeLength(b));
  return shorter > 0 ? overlap / shorter : 0;
}

function clipMainScore(clip: ClipFragment): number {
  if (!clip.impactScore) return -Infinity;
  const { main } = destructureImpactScore(clip.impactScore);
  return main ?? -Infinity;
}

function deduplicateClips(clips: ClipFragment[]): ClipFragment[] {
  // Process best-first so the clip we keep in each overlap cluster is the
  // highest-impact one; ties broken by the longer (more complete) segment.
  const ranked = [...clips].sort((a, b) => {
    const scoreDiff = clipMainScore(b) - clipMainScore(a);
    if (scoreDiff !== 0) return scoreDiff;
    return (
      rangeLength(b.transcriptPosition) - rangeLength(a.transcriptPosition)
    );
  });

  const kept: ClipFragment[] = [];
  for (const clip of ranked) {
    const isNearDuplicate = kept.some(
      k =>
        overlapRatio(k.transcriptPosition, clip.transcriptPosition) >=
        OVERLAP_DEDUP_THRESHOLD,
    );
    if (!isNearDuplicate) {
      kept.push(clip);
    }
  }

  logger.info(
    `deduplicateClips: ${kept.length} unique clips after overlap dedup (${clips.length - kept.length} near-dupes removed, threshold ${OVERLAP_DEDUP_THRESHOLD})`,
  );

  return kept;
}

function timecodeToSeconds(timecode: string): number {
  // Check if it's in hh:mm:ss format
  if (timecode.includes(':')) {
    const parts = timecode.split(':');
    if (parts.length === 3) {
      const hours = parseFloat(parts[0]);
      const minutes = parseFloat(parts[1]);
      const seconds = parseFloat(parts[2]);
      return hours * 3600 + minutes * 60 + seconds;
    } else if (parts.length === 2) {
      const minutes = parseFloat(parts[0]);
      const seconds = parseFloat(parts[1]);
      return minutes * 60 + seconds;
    }
  }
  // Otherwise, assume it's already in seconds
  return parseFloat(timecode);
}

function findTranscriptElementsByTimecode(
  startTimecode: string,
  endTimecode: string,
  transcriptionElements: TranscriptElement[],
): {
  startIndex: number;
  endIndex: number;
  startTime: number;
  endTime: number;
} | null {
  const startSeconds = timecodeToSeconds(startTimecode);
  const endSeconds = timecodeToSeconds(endTimecode);

  let startIndex = -1;
  let endIndex = -1;

  // Find the first element at or after startSeconds
  for (let i = 0; i < transcriptionElements.length; i++) {
    const element = transcriptionElements[i];
    if (element.ts != null && element.ts >= startSeconds) {
      startIndex = i;
      break;
    }
  }

  // Find the last element at or before endSeconds
  for (let i = transcriptionElements.length - 1; i >= 0; i--) {
    const element = transcriptionElements[i];
    if (element.end_ts != null && element.end_ts <= endSeconds) {
      endIndex = i;
      break;
    }
  }

  if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
    return null;
  }

  return {
    startIndex,
    endIndex,
    startTime: startSeconds,
    endTime: endSeconds,
  };
}

export function buildClipFragment({
  partIndex,
  startIndex,
  endIndex,
  startTime,
  endTime,
  title,
  textFragment,
  transcriptionElements,
  originalVideo,
  platform,
  theme,
  themeDisplayName,
  durationMin,
  durationMax,
  speakers,
  stepContext,
  prevResultFragments,
  impactScore,
  videoTemplateId,
}: {
  partIndex: number;
  startIndex: number;
  endIndex: number;
  startTime: number;
  endTime: number;
  title: string;
  textFragment?: string;
  transcriptionElements: TranscriptElement[];
  originalVideo: StoryVideo;
  platform?: string;
  theme: string;
  themeDisplayName?: string;
  durationMin: number;
  durationMax: number;
  speakers: Speaker[];
  stepContext: AIFlowStep;
  prevResultFragments: ClipFragment[];
  impactScore?: Record<string, number>;
  videoTemplateId?: string;
}): ClipFragment | null {
  const fragmentDuration = endTime - startTime;

  const duplicateIndexCheck = prevResultFragments.findIndex(
    fragment =>
      fragment.transcriptPosition.startIndex === startIndex &&
      fragment.transcriptPosition.endIndex === endIndex,
  );
  if (duplicateIndexCheck !== -1) {
    stepContext.log(
      `Fragment ${partIndex} is a duplicate of ${duplicateIndexCheck}`,
    );
    return null;
  }

  const withinDuration =
    fragmentDuration >= durationMin && fragmentDuration <= durationMax;

  if (!withinDuration) {
    stepContext.log(
      `Fragment ${partIndex} has duration ${fragmentDuration}s (outside ${durationMin}-${durationMax}s range)`,
    );
    return null;
  }

  stepContext.log(
    `Fragment ${partIndex} has duration ${fragmentDuration}s (ok)`,
  );
  // Check if this fragment has a predominant speaker
  const fragmentElements = transcriptionElements.slice(
    startIndex,
    Math.min(endIndex + 1, transcriptionElements.length),
  );

  // Count speaker occurrences in this fragment
  const speakerCounts: Record<string, number> = {};
  let predominantSpeaker: string | null = null;
  let predominantSpeakerLabel: string | null = null;

  // Count speakers in the fragment
  fragmentElements.forEach(element => {
    if (element.speaker_label) {
      speakerCounts[element.speaker_label] =
        (speakerCounts[element.speaker_label] || 0) + 1;
    }
  });

  // Find predominant speaker if any
  if (Object.keys(speakerCounts).length > 0) {
    const entries = Object.entries(speakerCounts);
    entries.sort((a, b) => b[1] - a[1]); // Sort by count descending

    const totalElements = fragmentElements.filter(
      element => element.speaker_label,
    ).length;
    const [topSpeakerLabel, topSpeakerCount] = entries[0];

    if (topSpeakerCount / totalElements >= 0.6) {
      predominantSpeakerLabel = topSpeakerLabel;

      if (predominantSpeakerLabel) {
        predominantSpeaker =
          speakers.find(
            speaker => speaker.speakerLabel === predominantSpeakerLabel,
          )?.name || null;
      }
    }
  }

  // Discard clips where the predominant speaker is not one of the target speakers
  if (speakers.length > 0) {
    const targetLabels = speakers
      .flatMap(s => s.allSpeakerLabels ?? [s.speakerLabel])
      .filter(Boolean);
    if (
      !predominantSpeakerLabel ||
      !targetLabels.includes(predominantSpeakerLabel)
    ) {
      stepContext.log(
        `Fragment ${partIndex} discarded: predominant speaker "${predominantSpeakerLabel ?? 'none'}" is not in target speakers [${targetLabels.join(', ')}]`,
      );
      return null;
    }
  }

  // Extract text from fragment elements if not provided
  const text = textFragment || extractTranscriptionText(fragmentElements);

  return {
    title: title || '',
    text,
    duration: fragmentDuration,
    startTime,
    src:
      originalVideo?.video?.mp4UrlLow ||
      originalVideo?.video?.mp4UrlMedium ||
      originalVideo?.video?.mp4Url ||
      originalVideo?.url ||
      '',
    muxPlaybackId: originalVideo?.video?.muxPlaybackId,
    platform,
    theme,
    themeDisplayName,
    transcriptPosition: {
      startIndex,
      endIndex: Math.min(endIndex + 1, transcriptionElements.length - 1),
    },
    ...(impactScore && { impactScore }),
    // Include speaker information if available
    ...(predominantSpeaker && { speakerName: predominantSpeaker }),
    ...(predominantSpeakerLabel && {
      speakerLabel: predominantSpeakerLabel,
    }),
    ...(videoTemplateId && { videoTemplateId }),
    ...(!withinDuration && { durationRelaxed: true }),
  };
}

function getTextClipsFromResponse({
  transcriptionElements,
  originalVideo,
  platform,
  theme,
  themeDisplayName,
  durationMin,
  durationMax,
  speakers,
  stepContext,
  prevResponseFragmentsNumber,
  prevResultFragments,
  videoTemplateId,
}: {
  transcriptionElements: TranscriptElement[];
  originalVideo: StoryVideo;
  platform?: string;
  theme: string;
  themeDisplayName?: string;
  durationMin: number;
  durationMax: number;
  speakers: Speaker[];
  stepContext: AIFlowStep;
  prevResponseFragmentsNumber: number;
  prevResultFragments: ClipFragment[];
  videoTemplateId?: string;
}): (ClipFragment | null)[] {
  const resultParts = (stepContext.lastResponse?.slice(
    prevResponseFragmentsNumber,
  ) || []) as {
    title: string;
    text_fragment: string;
    impact_score: Record<string, number>;
  }[];

  const NUMBER_OF_WORDS_TO_MATCH = 5;

  if (resultParts.length === 0) {
    return [];
  }

  const resultFragments: (ClipFragment | null)[] = resultParts.map(
    (part, index) => {
      const partIndex = prevResponseFragmentsNumber + index;
      const fragmentWords = stripSpeakerMarkers(part.text_fragment)
        .trim()
        .split(' ');
      const beginning = stripPunctuationFromEnds(
        fragmentWords.slice(0, NUMBER_OF_WORDS_TO_MATCH).join(' '),
      );
      const ending = stripPunctuationFromEnds(
        fragmentWords.slice(-NUMBER_OF_WORDS_TO_MATCH).join(' '),
      );

      stepContext.log(
        `Fragment ${partIndex}: \nbeginning: ${beginning}, \nending: ${ending}`,
      );

      const {
        startTime: beginningTime,
        transcriptPosition: beginningTranscriptPosition,
      } = getFragmentPosition(beginning, transcriptionElements) || {};
      if (beginningTime == null || !beginningTranscriptPosition) {
        stepContext.log(`Fragment ${partIndex} has no beginning position.`);
        return null;
      }

      const {
        endTime: endingTime,
        transcriptPosition: endingTranscriptPosition,
      } =
        getFragmentPosition(
          ending,
          transcriptionElements,
          beginningTranscriptPosition.startIndex,
        ) || {};
      if (endingTime == null || !endingTranscriptPosition) {
        stepContext.log(`Fragment ${partIndex} has no ending position.`);
        return null;
      }

      const fragmentWordsCount = fragmentWords.length;
      stepContext.log(`Fragment words: ${fragmentWordsCount}`);

      return buildClipFragment({
        partIndex,
        startIndex: beginningTranscriptPosition.startIndex,
        endIndex: endingTranscriptPosition.endIndex,
        startTime: beginningTime,
        endTime: endingTime,
        title: part.title,
        // textFragment: part.text_fragment,
        transcriptionElements,
        originalVideo,
        platform,
        theme,
        themeDisplayName,
        durationMin,
        durationMax,
        speakers,
        stepContext,
        prevResultFragments,
        impactScore: part.impact_score,
        videoTemplateId,
      });
    },
  );

  return resultFragments;
}

function getTimecodedClipFromResponse({
  transcriptionElements,
  originalVideo,
  platform,
  theme,
  themeDisplayName,
  durationMin,
  durationMax,
  speakers,
  stepContext,
  prevResponseFragmentsNumber,
  prevResultFragments,
  videoTemplateId,
}: {
  transcriptionElements: TranscriptElement[];
  originalVideo: StoryVideo;
  platform?: string;
  theme: string;
  themeDisplayName?: string;
  durationMin: number;
  durationMax: number;
  speakers: Speaker[];
  stepContext: AIFlowStep;
  prevResponseFragmentsNumber: number;
  prevResultFragments: ClipFragment[];
  videoTemplateId?: string;
}): (ClipFragment | null)[] {
  const resultParts = (stepContext.lastResponse?.slice(
    prevResponseFragmentsNumber,
  ) || []) as {
    title: string;
    start: string;
    end: string;
  }[];

  if (resultParts.length === 0) {
    return [];
  }

  const resultFragments: (ClipFragment | null)[] = resultParts.map(
    (part, index) => {
      const partIndex = prevResponseFragmentsNumber + index;

      stepContext.log(
        `Fragment ${partIndex}: \ntitle: ${part.title}, \nstart: ${part.start}, \nend: ${part.end}`,
      );

      const transcriptMatch = findTranscriptElementsByTimecode(
        part.start,
        part.end,
        transcriptionElements,
      );

      if (!transcriptMatch) {
        stepContext.log(
          `Fragment ${partIndex} has no matching transcript elements.`,
        );
        return null;
      }

      const { startIndex, endIndex, startTime, endTime } = transcriptMatch;

      return buildClipFragment({
        partIndex,
        startIndex,
        endIndex,
        startTime,
        endTime,
        title: part.title,
        transcriptionElements,
        originalVideo,
        platform,
        theme,
        themeDisplayName,
        durationMin,
        durationMax,
        speakers,
        stepContext,
        prevResultFragments,
        videoTemplateId,
      });
    },
  );

  return resultFragments;
}

/**
 * Parse an index field from the AI response. Handles:
 * - Integer: 318
 * - String integer: "318"
 * - Pipe-separated: "318|54.59" (index|timestamp from transcriptWithIndices format)
 */
export function parseIndexField(
  value: string | number | undefined,
): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  const str = String(value);
  // Handle pipe-separated format: take the first part (index)
  const indexPart = str.includes('|') ? str.split('|')[0] : str;
  const parsed = parseInt(indexPart, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

const indexedClipPartSchema = z
  .object({
    title: z.string(),
    // AI returns indices as start_index/end_index (integer) or start/end (string like "318|54.59")
    start_index: z.number().optional(),
    end_index: z.number().optional(),
    start: z.union([z.string(), z.number()]).optional(),
    end: z.union([z.string(), z.number()]).optional(),
    text: z.string().optional(),
    impact_score: z.record(z.string(), z.number()).optional(),
  })
  .refine(data => data.start_index != null || data.start != null, {
    message: 'Either start_index or start is required',
  })
  .refine(data => data.end_index != null || data.end != null, {
    message: 'Either end_index or end is required',
  });

export function getIndexedClipFromResponse({
  transcriptionElements,
  originalVideo,
  platform,
  theme,
  themeDisplayName,
  durationMin,
  durationMax,
  speakers,
  stepContext,
  prevResponseFragmentsNumber,
  prevResultFragments,
  videoTemplateId,
  enableTextVerification = false,
}: {
  transcriptionElements: TranscriptElement[];
  originalVideo: StoryVideo;
  platform?: string;
  theme: string;
  themeDisplayName?: string;
  durationMin: number;
  durationMax: number;
  speakers: Speaker[];
  stepContext: AIFlowStep;
  prevResponseFragmentsNumber: number;
  prevResultFragments: ClipFragment[];
  videoTemplateId?: string;
  enableTextVerification?: boolean;
}): (ClipFragment | null)[] {
  const lastResponse = stepContext.lastResponse;
  if (!Array.isArray(lastResponse)) {
    return [];
  }

  const rawParts: unknown[] = lastResponse.slice(prevResponseFragmentsNumber);

  if (rawParts.length === 0) {
    return [];
  }

  // Parse each response part with zod for type safety and clear error messaging
  const resultParts = rawParts.map((raw: unknown, i: number) => {
    const parsed = indexedClipPartSchema.safeParse(raw);
    if (!parsed.success) {
      logger.warn(
        `Fragment ${prevResponseFragmentsNumber + i} failed schema validation`,
        {
          errors: parsed.error.issues.map(
            issue => `${issue.path.join('.')}: ${issue.message}`,
          ),
          raw: JSON.stringify(raw).slice(0, 200),
        },
      );
      return null;
    }
    return parsed.data;
  });

  const validParts = resultParts.filter(
    (p): p is z.infer<typeof indexedClipPartSchema> => p !== null,
  );

  if (validParts.length === 0) {
    return [];
  }

  const resultFragments: (ClipFragment | null)[] = validParts.map(
    (part, index) => {
      const partIndex = prevResponseFragmentsNumber + index;
      // Support both field name conventions and parse pipe-separated format (e.g., "318|54.59")
      const startIndex = part.start_index ?? parseIndexField(part.start);
      const endIndex = part.end_index ?? parseIndexField(part.end);

      stepContext.log(
        `Fragment ${partIndex}: \ntitle: ${part.title}, \nstart_index: ${startIndex}, \nend_index: ${endIndex}`,
      );

      if (
        startIndex == null ||
        endIndex == null ||
        startIndex < 0 ||
        endIndex >= transcriptionElements.length ||
        startIndex > endIndex
      ) {
        logger.warn(`Fragment ${partIndex} rejected: invalid indices`, {
          startIndex,
          endIndex,
          elementsLength: transcriptionElements.length,
        });
        return null;
      }

      // Find nearest elements with valid timestamps (indices may land on punct elements)
      let startEl: TranscriptElement | undefined;
      for (let si = startIndex; si <= endIndex; si++) {
        const el = transcriptionElements[si];
        if (el && el.ts != null) {
          startEl = el;
          break;
        }
      }

      let endEl: TranscriptElement | undefined;
      for (let ei = endIndex; ei >= startIndex; ei--) {
        const el = transcriptionElements[ei];
        if (el && el.end_ts != null) {
          endEl = el;
          break;
        }
      }

      if (!startEl || !endEl || startEl.ts == null || endEl.end_ts == null) {
        logger.warn(
          `Fragment ${partIndex} rejected: no valid timestamps in range`,
          {
            startIndex,
            endIndex,
            startElType: transcriptionElements[startIndex]?.type,
            endElType: transcriptionElements[endIndex]?.type,
          },
        );
        return null;
      }

      const startTime = startEl.ts - (startEl.buffer_before_ts || 0);
      const endTime = endEl.end_ts + (endEl.buffer_after_ts || 0);

      // Verify AI's intended text matches the actual transcript at these indices
      if (enableTextVerification && part.text) {
        const actualText = transcriptionElements
          .slice(
            startIndex,
            Math.min(endIndex + 1, transcriptionElements.length),
          )
          .filter(
            el => el.value && el.state !== 'removed' && el.state !== 'cut',
          )
          .map(el => el.value)
          .join(' ');

        const similarity = wordOverlapSimilarity(
          part.text,
          actualText.split(/\s+/).slice(0, 25).join(' '),
        );
        if (similarity < 0.5) {
          logger.warn(
            `Fragment ${partIndex} text mismatch: AI text doesn't match transcript at indices`,
            {
              similarity: similarity.toFixed(2),
              startIndex,
              endIndex,
              aiText: part.text.slice(0, 80),
              actualText: actualText.slice(0, 80),
            },
          );
          return null;
        }
      }

      return buildClipFragment({
        partIndex,
        startIndex,
        endIndex,
        startTime,
        endTime,
        title: part.title,
        transcriptionElements,
        originalVideo,
        platform,
        theme,
        themeDisplayName,
        durationMin,
        durationMax,
        speakers,
        stepContext,
        prevResultFragments,
        impactScore: part.impact_score,
        videoTemplateId,
      });
    },
  );

  return resultFragments;
}

function createClipResultMapper({
  transcriptionElements,
  originalVideo,
  platform,
  theme,
  themeDisplayName,
  durationMin,
  durationMax,
  speakers,
  videoTemplateId,
  config,
}: {
  transcriptionElements: TranscriptElement[];
  originalVideo: StoryVideo;
  platform?: string;
  theme: string;
  themeDisplayName?: string;
  durationMin: number;
  durationMax: number;
  speakers: Speaker[];
  videoTemplateId?: string;
  config: ClipProducerConfig;
}): (flowContext: any, stepContext: AIFlowStep) => (ClipFragment | null)[] {
  return (flowContext: any, stepContext: AIFlowStep) => {
    const prevResponseFragmentsNumber =
      stepContext.context.previousResponseFragmentsNumber || 0;
    const prevResultFragments = (stepContext.context.previousResultFragments ||
      []) as ClipFragment[];

    if (
      !stepContext.lastResponse ||
      stepContext.lastResponse.length === prevResponseFragmentsNumber
    ) {
      if (stepContext.doneStreaming) {
        stepContext.context.previousResultFragments = [];
        stepContext.context.previousResponseFragmentsNumber = 0;
      }
      return prevResultFragments;
    }

    stepContext.context.previousResponseFragmentsNumber =
      stepContext.lastResponse?.length || 0;

    const handlerParams = {
      transcriptionElements,
      originalVideo,
      platform,
      theme,
      themeDisplayName,
      durationMin,
      durationMax,
      speakers,
      stepContext,
      prevResponseFragmentsNumber,
      prevResultFragments,
      videoTemplateId,
      enableTextVerification: config.enableTextVerification,
    };

    const resultFragments =
      stepContext.resultType === 'indices'
        ? getIndexedClipFromResponse(handlerParams)
        : stepContext.resultType === 'timecodes'
          ? getTimecodedClipFromResponse(handlerParams)
          : getTextClipsFromResponse(handlerParams);

    const notNullFragments = resultFragments.filter(Boolean) as ClipFragment[];
    const newResultFragments = prevResultFragments.concat(notNullFragments);
    stepContext.context.previousResultFragments = newResultFragments;

    if (stepContext.doneStreaming) {
      stepContext.context.previousResultFragments = [];
      stepContext.context.previousResponseFragmentsNumber = 0;
    }

    return newResultFragments;
  };
}

export const produceClipsChunked = async (params: {
  videoCreator: IVideoCreatorStore;
  submitTranscriptionText: string;
  transcriptionText: string;
  originalVideo: StoryVideo;
  originalDuration: number;
  transcriptionElements: TranscriptElement[];
  duration: number | null;
  theme: string;
  customTheme?: string;
  extraClipMetadata?: Partial<
    Pick<ClipFragment, 'customPrompt' | 'suggestedTheme'>
  >;
  themeDisplayName?: string;
  platform?: string;
  onClipsProduced?: (clips: ClipFragment[]) => void;
  abortToken: { id: string; aborted: boolean };
  rootStore: RootStore;
  speakers?: Speaker[]; // Selected speakers for speaker filter
  allSpeakers?: Speaker[]; // All speakers for transcript annotation
  goal?: GoalOption | null; // Optional goal selection
  audiences?: AudienceOption[]; // Optional audience selection
  videoTemplateId?: string; // Optional video template ID
  config?: ClipProducerConfig;
}) => {
  const config = { ...defaultClipProducerConfig, ...params.config };
  const {
    videoCreator,
    submitTranscriptionText: fullTranscriptionText,
    transcriptionText,
    transcriptionElements,
    duration,
    originalDuration,
    theme,
    customTheme,
    extraClipMetadata,
    platform,
    originalVideo,
    onClipsProduced,
    abortToken,
    rootStore,
    speakers = [],
    allSpeakers = [],
    goal = null,
    audiences = [],
    themeDisplayName,
    videoTemplateId,
  } = params;

  let [durationMin, durationMax] = [7, 120];
  if (duration) {
    [durationMin, durationMax] = getRangeForDuration(duration);
  }

  // Calculate speech pace (words/sec) per speaker and as a weighted average
  // for selected speakers, excluding long pauses (>1s) between speech segments.
  const originalVideoWords = transcriptionText.split(' ');
  const selectedSpeakerLabels: string[] = speakers
    .flatMap(
      s => s.allSpeakerLabels ?? (s.speakerLabel ? [s.speakerLabel] : []),
    )
    .filter((label): label is string => Boolean(label));
  const speechPace = calculateSpeechPace(
    transcriptionElements,
    selectedSpeakerLabels,
  );
  const speechWordsPerSecond = speechPace.average;

  if (speechWordsPerSecond === 0) {
    logger.warn(
      'Speech pace calculation returned 0; falling back to originalDuration-based estimate',
      {
        selectedSpeakerLabels,
        elementsCount: transcriptionElements.length,
        originalDuration,
      },
    );
  }

  // Calculate word counts based on actual speaking rate (not total video duration)
  const newVideoWords = Math.min(
    speechWordsPerSecond > 0
      ? Math.round(speechWordsPerSecond * ((durationMin + durationMax) / 2))
      : Math.round(
          (originalVideoWords.length * ((durationMin + durationMax) / 2)) /
            originalDuration,
        ),
    originalVideoWords.length,
  );
  const [clipLengthMin, clipLengthMax] = [durationMin, durationMax].map(dur =>
    Math.min(
      speechWordsPerSecond > 0
        ? Math.round(speechWordsPerSecond * dur)
        : Math.round((originalVideoWords.length * dur) / originalDuration),
      originalVideoWords.length,
    ),
  );

  let submitTranscriptionText = fullTranscriptionText;

  if (config.enableSpeakerFiltering && speakers && speakers.length > 0) {
    // Use our utility function to create a filtered transcript with speaker attribution
    const filteredTranscript = createSpeakerFilteredTranscript(
      transcriptionElements,
      speakers,
    );
    submitTranscriptionText = filteredTranscript.text;
  }

  const preset =
    customTheme && theme === 'Custom'
      ? rootStore.templatedPromptsStore.customClipProducerPreset
      : getFlowTitleAndCategoryByTheme(
          rootStore.templatedPromptsStore.clipProducerPresets,
          theme,
        );

  const OVERLAP_WORDS = Math.min(clipLengthMax, MAX_WORDS_OVERLAP);

  const chunks = chunkTranscription(
    submitTranscriptionText.split(' '),
    config.chunkSize,
    OVERLAP_WORDS,
  );

  const clipProducerAiFlow =
    await rootStore.datoClientStore.aiFlowRepository.load(preset);
  if (!clipProducerAiFlow) {
    logger.error('No clip producer AI flow found', { preset: preset.title });
    return [];
  }
  // Look up video template name from cached repository data (once, outside chunk loop)
  let videoTemplateName: string | undefined;
  if (videoTemplateId) {
    const templates =
      await rootStore.datoClientStore.videoTemplateRepository.getVideoTemplates(
        rootStore.templatedPromptsStore.videoTemplatesIds,
      );
    videoTemplateName = templates.find(t => t.id === videoTemplateId)?.label;
  }

  // Pass selected speaker names to the AI prompt — framing/instructions live in the Dato prompt template
  const speakerContext =
    speakers.length > 0
      ? speakers
          .map(s => `${s.name}${s.title ? ` (${s.title})` : ''}`)
          .join(', ')
      : '';

  // Build speaker label → name map for annotating transcript with speaker changes
  const speakerLabelToName = new Map<string, string>();
  for (const s of allSpeakers) {
    if (s.speakerLabel && s.name) {
      speakerLabelToName.set(s.speakerLabel, s.name);
    }
    // Also map consolidated labels
    if (s.allSpeakerLabels) {
      for (const label of s.allSpeakerLabels) {
        if (s.name) speakerLabelToName.set(label, s.name);
      }
    }
  }

  // Map each submitted word to its element index for chunk scoping
  const submittedWords = submitTranscriptionText.split(' ');
  const wordToElementIndex = mapWordsToElements(
    submittedWords,
    transcriptionElements,
    null, // No speaker filtering — full transcript is always sent
  );

  if (wordToElementIndex.length < submittedWords.length) {
    logger.warn(
      'Word-to-element mapping is incomplete; some chunks may use full elements as fallback',
      {
        mappedWords: wordToElementIndex.length,
        totalWords: submittedWords.length,
      },
    );
  }

  // Prepare an array of promises for all chunk flows
  const flowPromises = chunks.map(async ({ text: chunkText }, chunkIndex) => {
    if (abortToken.aborted) return [];
    const localAiFlowClone = clipProducerAiFlow.clone();

    let bufferedResults: ClipFragment[] = [];

    // title to log: email | Theme | Showcase | Story | chunk
    const userEmail =
      rootStore.userIdentityStore.currentIdentity?.email ?? 'unknown';
    const showcaseTitle =
      videoCreator.organization?.title ?? 'Unknown Showcase';
    const storyTitle = videoCreator.story?.title ?? 'Unknown Story';
    localAiFlowClone.title = `${userEmail} | Theme: ${preset.title} | Showcase: ${showcaseTitle} | Story: ${storyTitle} | chunk ${chunkIndex + 1}/${chunks.length}`;

    // Format goal and audiences
    let goalContext = '';
    if (goal && goal.id !== 'none') {
      // get aiprompt for goal
      const goalPrompt =
        await rootStore.datoClientStore.aiPromptRepository.getPromptByTitle(
          'Clip Producer | Context | Goal',
        );
      if (goalPrompt) {
        goalContext = `${goalPrompt.description}\n\n${goal.label}: ${goal.finalPrompt}`;
      }
    }
    let audiencesContext = '';
    if (audiences && audiences.length > 0) {
      // get aiprompt for audiences
      const audiencePrompt =
        await rootStore.datoClientStore.aiPromptRepository.getPromptByTitle(
          'Clip Producer | Context | Audiences',
        );
      if (audiencePrompt) {
        audiencesContext = `${audiencePrompt.description}\n\n${audiences
          .map(a => `${a.label}: ${a.finalPrompt}`)
          .join('\n\n')}`;
      }
    }

    // Scope transcript elements to this chunk's word range
    const chunkStartWord = chunks[chunkIndex].index;
    const chunkWordCount = chunkText.split(' ').length;
    const chunkEndWord = Math.min(
      chunkStartWord + chunkWordCount - 1,
      submittedWords.length - 1,
    );
    const chunkStartEl = wordToElementIndex[chunkStartWord] ?? 0;
    const chunkEndEl =
      wordToElementIndex[chunkEndWord] ?? transcriptionElements.length - 1;
    const chunkElements = transcriptionElements.slice(
      chunkStartEl,
      chunkEndEl + 1,
    );

    const filteredChunkElements = chunkElements.filter(
      el => el.state !== 'removed' && el.type === 'text',
    );

    const getChunkWithSpeakerLabelsFormatted = (
      chunkElements: TranscriptElement[],
      formatter: (el: TranscriptElement, globalIdx: number) => string,
      joinSeparator: string,
    ) => {
      let lastSpeaker = '';
      const parts: string[] = [];
      for (let localIdx = 0; localIdx < chunkElements.length; localIdx++) {
        const el = chunkElements[localIdx];
        if (el.state === 'removed' || el.type !== 'text') continue;
        // Insert speaker marker when speaker changes
        const speakerName = el.speaker_label
          ? (speakerLabelToName.get(el.speaker_label) ?? el.speaker_label)
          : '';
        if (speakerName && speakerName !== lastSpeaker) {
          parts.push(`\n[Speaker: ${speakerName}]`);
          lastSpeaker = speakerName;
        }
        const globalIdx = chunkStartEl + localIdx;
        parts.push(formatter(el, globalIdx));
      }
      return parts.join(joinSeparator);
    };

    localAiFlowClone.context = {
      storyTranscription: getChunkWithSpeakerLabelsFormatted(
        chunkElements,
        el => `${el.value}`,
        ' ',
      ),
      storyTranscriptionPlainText: chunkText,
      transcriptWithTimestamps: filteredChunkElements
        .map(el => `(word: ${el.value}, time: ${el.ts})`)
        .join(', '),
      transcriptCompact: filteredChunkElements
        .map(el => `${el.value}|${el.ts}`)
        .join(' '),
      transcriptWithIndices: getChunkWithSpeakerLabelsFormatted(
        chunkElements,
        (el, globalIdx) => `${el.value}|${globalIdx}|${el.ts}`,
        ' ',
      ),
      storyLength: chunkWordCount,
      storyDuration: originalDuration,
      chunksTotal: chunks.length,
      socialPlatform: platform,
      // Deprecated: use clipDurationMin/clipDurationMax instead. Remove after Dato prompt migration.
      clipLengthMin,
      clipLengthMax,
      clipLength: newVideoWords,
      clipDurationMin: durationMin,
      clipDurationMax: durationMax,
      clipTheme: customTheme || '',
      goalContext,
      audiencesContext,
      speakerContext,
    };

    await enrichAIFlowWithImpactScore(localAiFlowClone, rootStore);

    const identity = rootStore.userIdentityStore.currentIdentity;
    localAiFlowClone.logContext = {
      aiFlowId: clipProducerAiFlow.id,
      personId: rootStore.datoPersonStore.person?.id,
      storyId: videoCreator.story?.id,
      showcaseId: videoCreator.organization?.id,
      metadata: {
        // identity & environment
        user_email: identity?.email,
        user_id: identity?.id,
        dato_environment: rootStore.datoClientStore.environment,
        // chunk info
        chunk_index: chunkIndex,
        chunks_total: chunks.length,
        // clip producer selections
        clip_producer_selections: {
          theme: preset.title,
          custom_theme: customTheme || undefined,
          theme_display_name: themeDisplayName,
          platform,
          goal:
            goal && goal.id !== 'none'
              ? { id: goal.id, label: goal.label }
              : 'none',
          audiences:
            audiences.length > 0
              ? audiences.map(a => ({ id: a.id, label: a.label }))
              : undefined,
          speakers:
            speakers.length > 0
              ? speakers.map(s => ({
                  id: s.id,
                  name: s.name,
                  title: s.title,
                }))
              : undefined,
          duration,
          video_template: videoTemplateId
            ? { id: videoTemplateId, name: videoTemplateName }
            : undefined,
          // derived constraints (computed from selections above)
          derived: {
            duration_min: durationMin,
            duration_max: durationMax,
            clip_length_min: clipLengthMin,
            clip_length_max: clipLengthMax,
          },
        },
      },
    };
    for (const step of localAiFlowClone.steps) {
      step.loggingContext = {
        prompt_title: preset.title,
        prompt_type: 'clip_producer',
        storyId: videoCreator.story?.id,
        story_name: videoCreator.story?.title,
        user_email: identity?.email,
        user_id: identity?.id,
      };
    }

    localAiFlowClone.steps[0].stream = 'object';
    localAiFlowClone.steps[0].mapper = createClipResultMapper({
      transcriptionElements,
      originalVideo,
      platform,
      theme,
      themeDisplayName,
      durationMin,
      durationMax,
      speakers,
      videoTemplateId,
      config,
    });

    localAiFlowClone.steps[0].onPartialResult = (
      result: (ClipFragment | null)[],
    ) => {
      if (abortToken.aborted) throw new Error('Aborted');
      bufferedResults = result.filter(Boolean) as ClipFragment[];
      if (chunkIndex === 0 && result.length > 0) {
        // only first chunk is streamed, other chunks are buffered
        onClipsProduced && onClipsProduced(bufferedResults);
      }
    };

    // small delay to avoid overwhelming the AI service and network
    const delayMs = 1500 * chunkIndex;
    await new Promise(resolve => setTimeout(resolve, delayMs));

    await localAiFlowClone.run();
    if (abortToken.aborted) throw new Error('Aborted');
    return bufferedResults;
  });

  // Wait for each result in order and emit clips in order
  const allClips: ClipFragment[] = [];
  for (const flowPromise of flowPromises) {
    const chunkClips = await flowPromise;
    allClips.push(...chunkClips);
    if (onClipsProduced && chunkClips.length > 0) {
      onClipsProduced(allClips);
    }
  }

  const uniqueClips = deduplicateClips(allClips);

  // Separate clips that match the requested duration from those that don't
  const withinRange = uniqueClips.filter(clip => !clip.durationRelaxed);
  const outsideRange = uniqueClips.filter(clip => clip.durationRelaxed);

  let finalClips: ClipFragment[];
  if (withinRange.length > 0) {
    finalClips = withinRange;
  } else if (outsideRange.length > 0) {
    // No clips matched the duration range — return the closest ones
    const targetMidpoint = (durationMin + durationMax) / 2;
    const sorted = outsideRange.sort((a, b) => {
      const distA = Math.abs(a.duration - targetMidpoint);
      const distB = Math.abs(b.duration - targetMidpoint);
      return distA - distB;
    });
    finalClips = sorted;
    logger.info(
      `No clips within ${durationMin}-${durationMax}s range; returning ${finalClips.length} closest clips (nearest: ${finalClips[0].duration.toFixed(1)}s)`,
    );
  } else {
    finalClips = [];
  }

  const sortedClips = finalClips.sort((a, b) => a.startTime - b.startTime);

  // Attach generation context to fragments so it persists in ClipMetadata
  if (extraClipMetadata) {
    for (const clip of sortedClips) {
      Object.assign(clip, { ...extraClipMetadata });
    }
  }

  return sortedClips;
};

export const analyzeParamsForTheLackOfClips = ({
  aborted,
  durations,
  speakers,
  goal,
  audiences,
}: {
  aborted: boolean;
  durations: {
    selectedDuration: number | null;
    originalDuration: number;
  };
  speakers?: unknown[];
  goal?: GoalOption | null;
  audiences?: AudienceOption[];
}): { reason: string } | null => {
  if (aborted) return null;

  let reason: string | undefined = undefined;
  if (infeasibleDuration()) {
    reason =
      'No clips found. Try a shorter clip duration given the length of the video';
  } else if (speakers?.length) {
    reason =
      'No clips found for the given speaker and theme. Please try another selection.';
  } else if (
    (goal && goal.id !== 'none') ||
    (audiences && audiences.length > 0)
  ) {
    reason =
      'No clips found for the given goals or audience. Please try another selection.';
  } else {
    reason =
      'No clips found, please try a different selection (e.g. duration or theme)';
  }

  if (reason) {
    return { reason };
  }

  return null;

  function infeasibleDuration(): boolean {
    const { selectedDuration, originalDuration } = durations;
    if (selectedDuration == null) return false;

    // Percentage-based infeasibility heuristic:
    // If the requested clip duration consumes a large share of the original video
    // we expect very few (or zero) viable clips. Example: 45s video & 30s selection (≈66%).
    // Threshold selected empirically: 30%.
    const INFEASIBLE_DURATION_SHARE_THRESHOLD = 0.3;
    const ratio = selectedDuration / originalDuration;

    return ratio >= INFEASIBLE_DURATION_SHARE_THRESHOLD;
  }
};

export function getFragmentPosition(
  fragment: string,
  transcriptElements?: TranscriptElement[],
  startFromElementIndex?: number,
): {
  transcriptPosition: {
    startIndex: number;
    endIndex: number;
  };
  duration: number;
  startTime: number;
  endTime: number;
} | null {
  const targetSentence = removePunctuation(fragment);
  // loop through all transcript elements and track the index of the target sentence across multiple elements
  if (!transcriptElements || !targetSentence) return null;

  let targetSentenceCurrIndex = 0;
  let currElementIndex = startFromElementIndex ?? 0;
  let targetSentenceElementStartIndex = 0;
  let targetSentenceElementEndIndex = 0;
  let found = false;

  while (currElementIndex < (transcriptElements?.length - 1 || 0)) {
    const currentElement = transcriptElements[currElementIndex];
    const currElementValue = currentElement.value;

    // if element contains the start of the target sentence save it as the start of the index
    if (
      currElementValue &&
      currentElement?.state !== 'removed' &&
      currentElement?.state !== 'cut'
    ) {
      // if currElementValue is punctuation skip
      if (currElementValue && currElementValue.match(/^[.,;!?]$/)) {
        currElementIndex++;
        continue;
      } else if (
        equalsIgnoringCase(
          targetSentence.substring(
            targetSentenceCurrIndex,
            targetSentenceCurrIndex + currElementValue.length,
          ),
          currElementValue,
        )
      ) {
        if (targetSentenceCurrIndex === 0) {
          targetSentenceElementStartIndex = currElementIndex;
        }
        targetSentenceCurrIndex += currElementValue.length;
      } else {
        targetSentenceCurrIndex = 0;
        // Re-check if current element starts a new match
        if (
          equalsIgnoringCase(
            targetSentence.substring(0, currElementValue.length),
            currElementValue,
          )
        ) {
          targetSentenceElementStartIndex = currElementIndex;
          targetSentenceCurrIndex = currElementValue.length;
        }
      }
    }
    if (targetSentenceCurrIndex === targetSentence.length) {
      targetSentenceElementEndIndex = currElementIndex;
      found = true;
      break;
    }
    currElementIndex++;
  }

  // RETURN if not found
  if (!found) return null;

  let ts = null;
  let end_ts = null;

  // loop through all transcript elements and track the index of the target sentence across multiple elements
  currElementIndex = targetSentenceElementStartIndex;
  while (currElementIndex <= targetSentenceElementEndIndex) {
    const currElementFinal = transcriptElements[currElementIndex];

    if (currElementFinal) {
      if (ts === null && currElementFinal.ts != null) {
        ts = currElementFinal.ts - (currElementFinal.buffer_before_ts || 0);
      }
      if (currElementFinal.end_ts) {
        end_ts =
          currElementFinal.end_ts + (currElementFinal.buffer_after_ts || 0);
      }
    }
    currElementIndex++;
  }

  if (ts == null || end_ts == null) return null;

  return {
    transcriptPosition: {
      startIndex: targetSentenceElementStartIndex,
      endIndex: targetSentenceElementEndIndex,
    },
    duration: end_ts - ts,
    startTime: ts,
    endTime: end_ts,
  };
}

function stripPunctuationFromEnds(text: string) {
  return text.replaceAll(
    /^[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+|[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/g,
    '',
  );
}

// The chunk transcription sent to the AI is annotated with `[Speaker: Name]`
// markers (see getChunkWithSpeakerLabelsFormatted). The AI echoes those markers
// back into its returned text_fragment, so strip them before matching the
// fragment against the real transcript — otherwise the head/tail word windows
// contain marker tokens that never match and the clip is silently dropped.
function stripSpeakerMarkers(text: string) {
  return text.replaceAll(/\[Speaker:[^\]]*\]/g, ' ').replaceAll(/\s+/g, ' ');
}

export async function saveProducedClips(
  videoCreator: IVideoCreatorStore,
  clips: ClipFragment[],
  sourcePlatform: SourcePlatform,
  asReel: boolean = false,
  options?: SaveProducedClipsOptions,
): Promise<SaveProducedClipsResult> {
  const originalVideo = getOriginalVideo(videoCreator);
  const nowStart = performance.now();

  // A loaded, published base video (final in Content Studio, or a published
  // video open in Creator Studio) carries its own curated videoSource +
  // scenes, and the AI selected these clips against that timeline. Published
  // videos are always Mux-hosted, so getSourceReference already routes them to
  // full render below; we use this only to skip scene re-detection there so
  // the clip inherits the base video's scenes instead of raw detection scenes.
  const isLoadedBaseVideo = !!originalVideo.videoFilePrimary;

  // Check if we can use lightweight save (metadata only, no VideoEditor)
  const sourceRef = getSourceReference(videoCreator, originalVideo);
  // PLAT-11445: while the story's renditions are still producing there is no
  // video to render a clip from — the full-render path would build a
  // VideoEditor over the audio-first composition (the freeze class). Saving
  // metadata only is the only correct save; the clip resolves its media at
  // open once the 270p lands.
  const renditionPending = isCompressedRenditionPending(videoCreator.story);
  const enableLightweightClipSave =
    (options?.enableLightweightClipSave ?? false) || renditionPending;
  const useBackgroundQueue = options?.useBackgroundQueue ?? false;
  const canUseLightweight = enableLightweightClipSave && !!sourceRef;
  if (renditionPending && !canUseLightweight) {
    throw new Error(
      'Cannot save clips while the video is still processing: no story reference for a metadata-only save',
    );
  }

  // Background queue is only available for lightweight saves
  const canUseBackgroundQueue =
    useBackgroundQueue && canUseLightweight && sourceRef;

  if (canUseBackgroundQueue) {
    // Non-blocking path: queue clips for background save
    logger.debug('saveProducedClips: using background queue path', {
      clipCount: clips.length,
    });

    const result = queueClipsForBackgroundSave(
      clips,
      videoCreator,
      originalVideo,
      sourcePlatform,
      sourceRef,
      asReel,
      options?.reelTitle,
    );

    logger.info('saveProducedClips: clips queued for background save', {
      queuedCount: result.tempIds.length,
      tempIds: result.tempIds,
      timeToQueueMs: performance.now() - nowStart,
    });

    return {
      mode: 'queued',
      queuedCount: result.tempIds.length,
      tempIds: result.tempIds,
    };
  }

  let newVideos: Video[];

  if (canUseLightweight) {
    // Fast path: metadata only, no Processor needed (but still synchronous)
    logger.debug('saveProducedClips: using lightweight save path');
    newVideos = await saveLightweightClips(
      clips,
      videoCreator,
      originalVideo,
      sourcePlatform,
      sourceRef,
      asReel,
      options?.reelTitle,
    );
    await videoCreator.addMultipleVideosToStory(newVideos);
  } else {
    // Slow path: full render via Processor
    logger.debug('saveProducedClips: using full render path', {
      asReel,
      hasSourceRef: !!sourceRef,
      enableLightweightClipSave,
    });
    newVideos = await saveClips(
      originalVideo,
      clips,
      videoCreator,
      sourcePlatform,
      asReel,
      // Preserve the loaded base video's inherited scenes/cuts instead of
      // re-applying detection scenes, which would clobber its curated scenes.
      isLoadedBaseVideo,
    );
  }

  const nowClipsSaved = performance.now();
  const videoToLoad = newVideos[0];
  if (!videoToLoad?.id) {
    logger.warn('saveProducedClips: no video to load after save');
  } else if (sourcePlatform === 'creator-studio') {
    await videoCreator.loadVideo(videoToLoad.id);
    logger.debug('saveProducedClips: new clip loaded after save', {
      videoId: videoToLoad.id,
    });
  }
  const nowEnd = performance.now();
  logger.info('saveProducedClips: save timings', {
    totalClips: clips.length,
    usedLightweightPath: canUseLightweight,
    totalTimeMs: nowEnd - nowStart,
    timeToSaveClipsMs: nowClipsSaved - nowStart,
    timeToLoadVideoMs: nowEnd - nowClipsSaved,
  });

  return {
    mode: 'sync',
    savedCount: newVideos.length,
    savedIds: newVideos.map(v => v.id).filter(Boolean) as string[],
  };
}

/**
 * Saves the story's original video as a single full-length lightweight clip
 * ([0, duration]), attaches it to the story, and returns the resulting
 * OtherVideo so the caller can open it in the Content Studio clip preview.
 *
 * Sources everything from story.originalVideo because the ContentStudio context
 * has no loaded currentVideo (so the saveProducedClips path is unavailable).
 * Reuses the lightweight-clip builders and the store's addMultipleVideosToStory
 * helper rather than re-implementing the save/attach/insert orchestration.
 * Returns null when the original video has no usable mp4 source to crop from.
 */
export async function saveFullVideoAsLightweightClip(
  videoCreator: IVideoCreatorStore,
  title: string,
): Promise<OtherVideo | null> {
  try {
    const story = videoCreator.story;
    const originalVideo = story?.originalVideo;
    if (!story || !originalVideo) {
      logger.warn('saveFullVideoAsLightweightClip: missing story or original');
      return null;
    }

    const duration = originalVideo.video?.duration;
    const sourceUrl =
      originalVideo.video?.mp4UrlLow ||
      originalVideo.video?.mp4UrlMedium ||
      originalVideo.video?.mp4Url ||
      originalVideo.url;
    if (!duration || !sourceUrl) {
      logger.warn(
        'saveFullVideoAsLightweightClip: original has no usable mp4 source or duration',
        { hasDuration: !!duration, hasSourceUrl: !!sourceUrl },
      );
      return null;
    }

    // getDefaultAspectRatio derives from story.originalVideo's dimensions
    // (including the square case) and falls back to 16:9.
    const aspectRatio = videoCreator.getDefaultAspectRatio();

    // A clip spanning the whole video; its source is expanded from the story on
    // first open (same mechanism AI clips use).
    const clipJson: ClipMetadata = {
      startTime: 0,
      duration,
      sourceStoryId: story.id,
      sourceUrl,
      isLightweightClip: true,
    };

    // A full-length copy covers the whole story, so its verbatim text is the
    // story transcript. Without it the card shows no transcript preview and the
    // clip picker's text search can't reach it, unlike every other clip.
    // Content Studio loads the transcript on mount, so this is normally already
    // in memory; an empty string just leaves the preview blank as before.
    await videoCreator.loadTranscriptIfNeeded();
    const transcriptionText =
      videoCreator.rootStore.transcriptionStore.getTranscriptionText();

    const saved =
      await videoCreator.datoClientStore.videoRepository.saveLightweightVideo(
        buildLightweightVideoFromParts(
          title,
          aspectRatio,
          'content-studio',
          clipJson,
          videoCreator,
          transcriptionText,
        ),
      );

    const newClip: OtherVideo = {
      ...createOptimisticOtherVideoFromParts(
        saved.id,
        title,
        aspectRatio,
        'content-studio',
        clipJson,
        videoCreator,
        transcriptionText,
      ),
      slug: saved.slug,
      hash: saved.hash,
    };

    // Attaches to the story and prepends to story.otherVideos in one step.
    await videoCreator.addMultipleVideosToStory([newClip as unknown as Video]);

    return newClip;
  } catch (err) {
    logger.error(
      'Failed to save full video as lightweight clip',
      extractErrorInfo(err),
    );
    return null;
  }
}

/**
 * Saves N source ranges as one reel — a lightweight clip carrying `isReel` and
 * `subClips` — attaches it to the story, and returns the resulting OtherVideo so
 * the caller can open it in the Content Studio clip preview (PLAT-11049).
 *
 * The reel equivalent of {@link saveFullVideoAsLightweightClip}, and it exists
 * for the same reason: Content Studio has no loaded `currentVideo`, so
 * `saveProducedClips` — which starts by reading it — is unavailable there.
 * Everything is sourced from `story.originalVideo` instead.
 *
 * `subClips` are written in the order the fragments are given (selection order,
 * epic DE11); `expandLightweightClip` re-crops from them in that same order.
 * Returns null when there is no usable mp4 source or no ranges at all. One
 * range is a legitimate reel — a reel of one Section, which is what the
 * explicit Clip-to-Reel conversion saves (PLAT-11249); the two-clip minimum
 * is a *combine* rule, enforced where combining is offered.
 */
export async function saveLightweightReelFromStory(
  videoCreator: IVideoCreatorStore,
  fragments: ClipFragment[],
  title: string,
): Promise<OtherVideo | null> {
  try {
    const story = videoCreator.story;
    const originalVideo = story?.originalVideo;
    if (!story || !originalVideo) {
      logger.warn('saveLightweightReelFromStory: missing story or original');
      return null;
    }
    if (fragments.length === 0) {
      logger.warn('saveLightweightReelFromStory: no ranges to save', {
        fragmentCount: fragments.length,
      });
      return null;
    }

    const sourceUrl =
      originalVideo.video?.mp4UrlLow ||
      originalVideo.video?.mp4UrlMedium ||
      originalVideo.video?.mp4Url ||
      originalVideo.url;
    if (!sourceUrl) {
      logger.warn('saveLightweightReelFromStory: original has no mp4 source');
      return null;
    }

    const aspectRatio = videoCreator.getDefaultAspectRatio();
    const clipJson = buildReelMetadata(fragments, {
      storyId: story.id,
      sourceUrl,
    });
    const transcriptionText = combineClipTexts(fragments);

    const saved =
      await videoCreator.datoClientStore.videoRepository.saveLightweightVideo(
        buildLightweightVideoFromParts(
          title,
          aspectRatio,
          'content-studio',
          clipJson,
          videoCreator,
          transcriptionText,
        ),
      );

    const newClip: OtherVideo = {
      ...createOptimisticOtherVideoFromParts(
        saved.id,
        title,
        aspectRatio,
        'content-studio',
        clipJson,
        videoCreator,
        transcriptionText,
      ),
      slug: saved.slug,
      hash: saved.hash,
    };

    // Attaches to the story and prepends to story.otherVideos in one step.
    await videoCreator.addMultipleVideosToStory([newClip as unknown as Video]);

    logger.info('saveLightweightReelFromStory: saved', {
      videoId: saved.id,
      sectionCount: fragments.length,
      storyId: story.id,
    });
    return newClip;
  } catch (err) {
    logger.error('saveLightweightReelFromStory: failed', extractErrorInfo(err));
    return null;
  }
}

/**
 * Queue clips for background save via WriteQueueStore.
 * Returns immediately without waiting for saves to complete.
 *
 * This function also adds optimistic OtherVideo entries to story.otherVideos
 * immediately, so clips appear in the UI instantly. When the background save
 * completes, the temp ID is replaced with the real ID.
 */
function queueClipsForBackgroundSave(
  clips: ClipFragment[],
  videoCreator: IVideoCreatorStore,
  originalVideo: Video,
  sourcePlatform: SourcePlatform,
  sourceRef: SourceReference,
  asReel: boolean = false,
  customReelTitle?: string,
): { tempIds: string[] } {
  const storyId = videoCreator.story?.id;
  if (!storyId) {
    throw new Error('Cannot queue clips: story ID not set');
  }

  const writeQueueStore = videoCreator.rootStore.writeQueueStore;

  const buildEntry = (
    tempId: string,
    clipMetadata: ClipMetadata,
    title: string,
    transcriptionText?: string,
  ) => {
    const video = buildLightweightVideoFromParts(
      title,
      originalVideo.aspectRatio,
      sourcePlatform,
      clipMetadata,
      videoCreator,
      transcriptionText,
    );
    const optimisticVideo = createOptimisticOtherVideoFromParts(
      tempId,
      title,
      originalVideo.aspectRatio,
      sourcePlatform,
      clipMetadata,
      videoCreator,
      transcriptionText,
    );
    return { tempId, optimisticVideo, video };
  };

  let entries: ReturnType<typeof buildEntry>[];

  if (asReel) {
    const tempId = generateTempClipId();
    const reelMetadata = buildReelMetadata(clips, sourceRef);
    const reelTitle =
      customReelTitle || getReelTitle(videoCreator, originalVideo);
    const reelText = combineClipTexts(clips);
    entries = [buildEntry(tempId, reelMetadata, reelTitle, reelText)];
  } else {
    entries = clips.map(clip => {
      const tempId = generateTempClipId();
      const clipMetadata = buildClipMetadata(clip, sourceRef);
      const title = clip.title || `${originalVideo.title} - ${clip.theme}`;
      return buildEntry(tempId, clipMetadata, title, clip.text);
    });
  }

  // Add optimistic videos to story.otherVideos IMMEDIATELY for instant UI feedback
  const optimisticVideos = entries.map(e => e.optimisticVideo);
  videoCreator.addOptimisticVideos(optimisticVideos);

  logger.debug('queueClipsForBackgroundSave: added optimistic videos', {
    count: optimisticVideos.length,
    tempIds: entries.map(e => e.tempId),
    asReel,
  });

  // Enqueue clips with callbacks to replace temp IDs when save completes
  const clipsToQueue = entries.map(({ tempId, video }) => ({
    id: tempId,
    video: video as LightweightVideoDTO,
    storyId,
    onComplete: (realId: string, slug: string, hash: string) => {
      videoCreator.replaceOptimisticVideoId(tempId, realId, slug, hash);
      logger.debug('queueClipsForBackgroundSave: replaced temp ID', {
        tempId,
        realId,
        slug,
      });
    },
    onError: (error: Error) => {
      logger.error('queueClipsForBackgroundSave: clip save failed', {
        tempId,
        error: error.message,
      });
      // Keep the optimistic entry with "Saving" state - user can retry via SaveStatusIndicator
    },
  }));

  const tempIds = writeQueueStore.enqueueClips(clipsToQueue);

  return { tempIds };
}

async function saveClips(
  originalVideo: Video,
  clips: ClipFragment[] | TranscriptClipFragment[],
  videoCreator: IVideoCreatorStore,
  sourcePlatform: SourcePlatform,
  asReel: boolean = false,
  skipSceneDetections: boolean = false,
): Promise<Video[]> {
  if (!originalVideo) throw new Error('Original video id missing');

  // Full render path via Processor (VideoEditor + S3 upload)
  // When skipSceneDetections is true (e.g. save-as-clip-from-transcript),
  // preserve the original video's scene crops instead of re-applying detections.
  const sceneDetections = skipSceneDetections
    ? undefined
    : getSceneDetectionsForCommand(videoCreator);
  const clipProcessor = new AIClipProcessor(
    clips,
    clipProcessorDepsFromStore(videoCreator),
    originalVideo,
    sourcePlatform,
    sceneDetections,
  );

  let newVideos: Video[];
  if (asReel) {
    newVideos = [await clipProcessor.saveAsReel()];
  } else {
    newVideos = await clipProcessor.saveIndividually();
  }

  await videoCreator.addMultipleVideosToStory(newVideos);
  logger.info('saveClips: done adding videos to story', {
    storyId: videoCreator.story?.id,
    newVideos: newVideos.map(v => v.id),
  });
  return newVideos;
}

export async function saveClipFromTranscript(
  videoCreator: IVideoCreatorStore,
  clip: TranscriptClipFragment,
  options?: {
    /** When true, skip loading the newly created clip into the editor */
    skipLoadAfterSave?: boolean;
  },
): Promise<{ id: string; title: string } | null> {
  logger.debug('saveClipFromTranscript: start', { clip, options });

  if (videoCreator.currentVideo?.id) {
    // Two-phase save: prepare (await) then background save
    const preparedVideo =
      await backgroundVideoSaveService.prepare(videoCreator);
    // Fire-and-forget actual save
    backgroundVideoSaveService.save(preparedVideo, videoCreator);
    logger.debug(
      'saveClipFromTranscript: current story and video will be saved in the background',
    );
  } else {
    await videoCreator.saveStoryAndVideo();
    logger.debug('saveClipFromTranscript: current story and video saved');
  }

  await videoCreator.prepareCurrentVideoForSave(); // ensure fresh data from stores
  const originalVideo = videoCreator.currentVideo;
  if (!originalVideo) {
    throw new Error('No video source found to create clip');
  }

  let savedVideo: Video | null = null;
  videoCreator.isSaving = true;
  try {
    [savedVideo] = await saveClips(
      getOriginalVideo(videoCreator),
      [clip],
      videoCreator,
      'creator-studio',
      false, // asReel
      true, // skipSceneDetections — preserve original scene crops
    );
  } catch (error) {
    logger.error('saveClipFromTranscript: error in parallel save', { error });
  } finally {
    videoCreator.isSaving = false;
  }
  if (savedVideo?.id) {
    if (options?.skipLoadAfterSave) {
      logger.debug('saveClipFromTranscript: clip saved (skip load)', {
        videoId: savedVideo.id,
      });
    } else {
      await videoCreator.loadVideo(savedVideo.id);
      logger.debug('saveClipFromTranscript: new clip loaded', {
        videoId: savedVideo.id,
      });
    }
    return { id: savedVideo.id, title: savedVideo.title ?? '' };
  } else {
    logger.warn('saveClipFromTranscript: no video to load');
    return null;
  }
}

/**
 * Save a clip from the Transcript & Speakers tab.
 *
 * Simplified version of saveClipFromTranscript() that accepts an
 * IClipProcessorDeps (from clipProcessorDepsFromAdapter) and a pre-built
 * source video snapshot instead of relying on global VideoCreatorStore state.
 *
 * Skips:
 *  - Background save of current video (adapter handles its own persistence)
 *  - videoCreator.prepareCurrentVideoForSave()
 *  - videoCreator.isSaving flag
 *  - videoCreator.loadVideo() after save
 *
 * Keeps:
 *  - AIClipProcessor crop → serialize → S3 → DatoCMS pipeline
 *  - deps.addMultipleVideosToStory() to update sidebar clip list
 *
 * See: docs/refactors/transcript-dependency-injection.md (Step 10)
 */
export async function saveClipFromTranscriptTab(
  deps: IClipProcessorDeps,
  sourceVideo: Video,
  clip: TranscriptClipFragment,
): Promise<{ id: string; title: string } | null> {
  logger.debug('saveClipFromTranscriptTab: start', { clip });

  if (!sourceVideo) {
    throw new Error('saveClipFromTranscriptTab: sourceVideo is required');
  }

  const sceneDetections = deps.sceneDetections ?? undefined;

  try {
    const clipProcessor = new AIClipProcessor(
      [clip],
      deps,
      sourceVideo,
      'creator-studio',
      sceneDetections,
    );

    const newVideos = await clipProcessor.saveIndividually();

    if (newVideos.length > 0) {
      await deps.addMultipleVideosToStory(newVideos);
      logger.info('saveClipFromTranscriptTab: clip saved and added to story', {
        videoId: newVideos[0].id,
        storyId: deps.story?.id,
      });
      return newVideos[0].id
        ? { id: newVideos[0].id, title: newVideos[0].title ?? '' }
        : null;
    }

    logger.warn(
      'saveClipFromTranscriptTab: AIClipProcessor returned no videos',
    );
    return null;
  } catch (error) {
    logger.error('saveClipFromTranscriptTab: failed', { error });
    throw error;
  }
}

function getOriginalVideo(videoCreator: IVideoCreatorStore): Video {
  if (!videoCreator.currentVideo) {
    throw new Error('Cannot get original video: currentVideo not set');
  }
  return toJS(videoCreator.currentVideo);
}

// --- Lightweight clip save functions (metadata only, no VideoEditor) ---

type SourceReference = {
  storyId: string;
  /**
   * CloudFront URL the clip was cut against — informational since
   * PLAT-11445: every surface resolves the clip's media from the story at
   * open (resolveClipMediaSource), so a clip saved while renditions were
   * pending is not pinned to the editor audio forever.
   */
  sourceUrl?: string;
};

function getSourceReference(
  videoCreator: IVideoCreatorStore,
  originalVideo: Video | StoryVideo,
): SourceReference | undefined {
  const storyId = videoCreator.story?.id;
  if (!storyId) {
    logger.debug('getSourceReference: missing storyId');
    return undefined;
  }

  // Check if this is a Mux video - if so, fall back to full save
  const muxPlaybackId =
    (originalVideo as StoryVideo).video?.muxPlaybackId ??
    (originalVideo as Video).videoFilePrimary?.video?.muxPlaybackId ??
    videoCreator.story?.originalVideo?.video?.muxPlaybackId;

  if (muxPlaybackId) {
    logger.debug(
      'getSourceReference: Mux video detected, falling back to full save',
    );
    return undefined;
  }

  // S3-hosted videos: capture the CloudFront URL from videoSource.elements as a
  // fallback/identifier. PLAT-11543: this may be absent (or the audio-first
  // source) when the clip is produced before the 270p exists — that's fine, the
  // clip resolves its real video from the story's current renditions at open, so
  // storyId alone is a valid lightweight reference. Returning here (rather than
  // undefined) keeps renditionless saves on the metadata path instead of the
  // full-render VideoEditor build, which hangs on an audio-first video.
  const videoSourceElements = (originalVideo as Video).videoSource?.elements;
  const sourceUrl =
    Array.isArray(videoSourceElements) && videoSourceElements.length > 0
      ? (videoSourceElements[0]?.source as string | undefined)
      : undefined;

  if (sourceUrl) {
    return { storyId, sourceUrl };
  }

  // No URL on the composition yet (mid-pending draft, or a composition that
  // has not been rendered): the story reference alone is enough — the media
  // is resolved at open.
  logger.debug('getSourceReference: no source URL found, story-only ref');
  return { storyId };
}

function buildClipMetadata(
  clip: ClipFragment,
  sourceRef: SourceReference,
): ClipMetadata {
  return {
    duration: clip.duration,
    startTime: clip.startTime,
    theme: clip.theme,
    speakerName: clip.speakerName,
    speakerLabel: clip.speakerLabel,
    impactScore: clip.impactScore,
    isReel: false,
    transcriptPosition: clip.transcriptPosition
      ? {
          startIndex: clip.transcriptPosition.startIndex,
          endIndex: clip.transcriptPosition.endIndex,
        }
      : undefined,
    sourceStoryId: sourceRef.storyId,
    sourceUrl: sourceRef.sourceUrl,
    isLightweightClip: true,
    videoTemplateId: clip.videoTemplateId,
    ...(clip.customPrompt && { customPrompt: clip.customPrompt }),
    ...(clip.suggestedTheme && { suggestedTheme: clip.suggestedTheme }),
  };
}

function buildReelMetadata(
  clips: ClipFragment[],
  sourceRef: SourceReference,
): ClipMetadata {
  const averagedImpactScore = averageImpactScores(
    clips.map(c => c.impactScore),
  );
  const customPrompt = clips.find(c => c.customPrompt)?.customPrompt;
  const suggestedTheme = clips.find(c => c.suggestedTheme)?.suggestedTheme;
  return {
    isReel: true,
    isLightweightClip: true,
    muxPlaybackId: clips[0].muxPlaybackId,
    duration: clips.reduce((sum, c) => sum + c.duration, 0),
    // The Section the reel opens on, not the earliest moment it draws from —
    // a reel's card thumbnail is a frame taken at `startTime`, so it has to be
    // the passage a viewer actually sees first. Playback comes off `subClips`,
    // so this is a display value only.
    startTime: clips[0].startTime,
    videoTemplateId: clips[0].videoTemplateId,
    sourceStoryId: sourceRef.storyId,
    sourceUrl: sourceRef.sourceUrl,
    theme: clips[0].theme,
    ...(customPrompt && { customPrompt }),
    ...(suggestedTheme && { suggestedTheme }),
    ...(averagedImpactScore && { impactScore: averagedImpactScore }),
    subClips: clips.map(clip => ({
      startTime: clip.startTime,
      duration: clip.duration,
      theme: clip.theme,
      impactScore: clip.impactScore,
      speakerLabel: clip.speakerLabel,
      speakerName: clip.speakerName,
      // DE8's seed, which `expandLightweightClip` turns into a name: Section
      // ids are minted by the crop, which has not run yet, so there is no key
      // to store a name under here.
      ...(clip.title?.trim() && { title: clip.title.trim() }),
      // PLAT-11067 — kept so a Section can always say which clip, or which
      // reel inside which reel, it came from.
      ...(clip.origin?.length && { origin: clip.origin }),
    })),
  };
}

function combineClipTexts(clips: ClipFragment[]): string {
  return clips
    .map(c => c.text)
    .filter(Boolean)
    .join('\n\n');
}

function getReelTitle(
  videoCreator: IVideoCreatorStore,
  originalVideo: Video,
): string {
  return reelPlaceholderTitle(videoCreator.story?.title ?? originalVideo.title);
}

export function buildLightweightVideoFromParts(
  title: string,
  aspectRatio: Video['aspectRatio'],
  sourcePlatform: SourcePlatform,
  clipMetadata: ClipMetadata,
  videoCreator: IVideoCreatorStore,
  transcriptionText?: string,
): {
  title: string;
  aspectRatio: string;
  sourcePlatform: string;
  clipJson: ClipMetadata;
  lastActionJson: VideoAction;
  isClientReady: boolean;
  transcriptionText?: string;
} {
  return {
    title,
    aspectRatio,
    sourcePlatform,
    clipJson: clipMetadata,
    lastActionJson: {
      editor: videoCreator.rootStore.userIdentityStore.currentEditor,
      date: new Date().getTime(),
      type: 'save',
    },
    isClientReady: !!videoCreator.story?.byExternalUser,
    transcriptionText,
  };
}

/**
 * Create an optimistic OtherVideo object for immediate UI display.
 * This allows clips to appear in the video list instantly while the
 * actual save happens in the background.
 */
export function createOptimisticOtherVideoFromParts(
  tempId: string,
  title: string,
  aspectRatio: Video['aspectRatio'],
  sourcePlatform: SourcePlatform,
  clipMetadata: ClipMetadata,
  videoCreator: IVideoCreatorStore,
  transcriptionText?: string,
): OtherVideo {
  const isClientReady = !!videoCreator.story?.byExternalUser;
  const currentEditor = videoCreator.rootStore.userIdentityStore.currentEditor;

  return {
    id: tempId,
    title,
    aspectRatio,
    clipJson: clipMetadata,
    lastActionJson: {
      editor: currentEditor,
      date: Date.now(),
      type: 'save',
    },
    sourcePlatform,
    isClientReady,
    videoStatus: 'editing',
    thumbnail: undefined,
    associatedVideos: [],
    _allReferencingSharedContents: [],
    _allReferencingCaptions: [],
    extraElementData: {},
    isHidden: false,
    isDownloadEnabled: false,
    transcriptionText,
  } as OtherVideo;
}

async function saveLightweightClips(
  clips: ClipFragment[],
  videoCreator: IVideoCreatorStore,
  originalVideo: Video,
  sourcePlatform: SourcePlatform,
  sourceRef: SourceReference,
  asReel: boolean = false,
  customReelTitle?: string,
): Promise<Video[]> {
  logger.debug('saveLightweightClips: start', {
    clipCount: clips.length,
    sourceStoryId: sourceRef.storyId,
    sourceUrl: sourceRef.sourceUrl,
    asReel,
  });

  const saveSingleVideo = async (
    title: string,
    clipMetadata: ClipMetadata,
    transcriptionText?: string,
  ): Promise<Video> => {
    const video = buildLightweightVideoFromParts(
      title,
      originalVideo.aspectRatio,
      sourcePlatform,
      clipMetadata,
      videoCreator,
      transcriptionText,
    );
    const saved =
      await videoCreator.datoClientStore.videoRepository.saveLightweightVideo(
        video,
      );
    logger.debug('saveLightweightClips: saved', {
      title,
      videoId: saved.id,
    });
    return {
      ...video,
      id: saved.id,
      slug: saved.slug,
      hash: saved.hash,
      videoSource: {},
      associatedVideos: [],
      extraElementData: {},
      videoStatus: 'editing' as const,
    } as Video;
  };

  if (asReel) {
    const reelMetadata = buildReelMetadata(clips, sourceRef);
    const reelTitle =
      customReelTitle || getReelTitle(videoCreator, originalVideo);
    const reelText = combineClipTexts(clips);
    const reelVideo = await saveSingleVideo(reelTitle, reelMetadata, reelText);

    logger.info('saveLightweightClips: reel saved', {
      videoId: reelVideo.id,
      subClipCount: clips.length,
    });
    return [reelVideo];
  }

  const results = await Promise.allSettled(
    clips.map(async clip => {
      const clipMetadata = buildClipMetadata(clip, sourceRef);
      const title = clip.title || `${originalVideo.title} - ${clip.theme}`;
      return saveSingleVideo(title, clipMetadata, clip.text);
    }),
  );

  const newVideos = results
    .filter((r): r is PromiseFulfilledResult<Video> => r.status === 'fulfilled')
    .map(r => r.value);

  logger.info('saveLightweightClips: done', {
    total: results.length,
    fulfilled: newVideos.length,
  });

  results.forEach((result, idx) => {
    if (result.status === 'rejected') {
      logger.error('saveLightweightClips: failed to save clip', {
        clipIndex: idx,
        reason: result.reason,
        clip: clips[idx],
      });
    }
  });

  return newVideos;
}

/**
 * Extracts scene detections from the videoCreator store and maps them
 * to the SceneDetection format expected by editor commands.
 */
function getSceneDetectionsForCommand(
  videoCreator: IVideoCreatorStore,
): SceneDetection[] | undefined {
  const sceneDetections: SceneDetection[] | null = videoCreator.sceneDetections;
  if (!sceneDetections || sceneDetections.length === 0) {
    return undefined;
  }
  return sceneDetections;
}
